import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { PublicFilters, CollectionList } from '../components/collections/CollectionList';
import CollectionDetail from '../components/collections/CollectionDetail';
import CardSearchModal from '../components/collections/CardSearchModal';
import CreateCollectionModal from '../components/collections/CreateCollectionModal';
import { pickLocalizedText } from '../utils/localizedContent';

const TCG_OPTIONS = ['pokemon', 'magic', 'yugioh', 'onepiece', 'riftbound'];


export default function Collections() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);
  
  // Pestaña y estado de colecciones
  const [tab, setTab] = useState(user ? 'my' : 'public');
  const [myCollections, setMyCollections] = useState([]);
  const [publicCollections, setPublicCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  
  // Estado de modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCardSearch, setShowCardSearch] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', is_public: false, tcg_type: 'mixed' });
  
  // Estado de búsqueda de cartas
  const [cardSearch, setCardSearch] = useState('');
  const [cardType, setCardType] = useState('pokemon');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addedCards, setAddedCards] = useState(new Set());
  
  // Filtros de colecciones públicas
  const [publicSearch, setPublicSearch] = useState('');
  const [publicTcgFilter, setPublicTcgFilter] = useState('');
  const [publicSortOrder, setPublicSortOrder] = useState('name_asc');

  // Cargar colecciones
  const fetchCollections = useCallback(async () => {
    if (user) {
      try { 
        const { data } = await axios.get(`${API}/collections/my`); 
        setMyCollections(data || []); 
      } catch (err) {
        console.error('Failed to fetch my collections:', err);
      }
    }
    try { 
      const { data } = await axios.get(`${API}/collections/public`); 
      setPublicCollections(data || []); 
    } catch (err) {
      console.error('Failed to fetch public collections:', err);
    }
  }, [user]);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);

  // Colecciones públicas filtradas y ordenadas
  const filteredPublicCollections = useMemo(() => {
    let filtered = [...publicCollections];
    
    if (publicSearch.trim()) {
      const searchLower = publicSearch.toLowerCase();
      filtered = filtered.filter(col => 
        col.name?.toLowerCase().includes(searchLower) ||
        col.user_name?.toLowerCase().includes(searchLower) ||
        col.description?.toLowerCase().includes(searchLower)
      );
    }
    
    if (publicTcgFilter) {
      filtered = filtered.filter(col => col.tcg_type === publicTcgFilter);
    }
    
    switch (publicSortOrder) {
      case 'name_asc': filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      case 'name_desc': filtered.sort((a, b) => (b.name || '').localeCompare(a.name || '')); break;
      case 'cards_desc': filtered.sort((a, b) => (b.cards?.length || 0) - (a.cards?.length || 0)); break;
      case 'cards_asc': filtered.sort((a, b) => (a.cards?.length || 0) - (b.cards?.length || 0)); break;
      default: break;
    }
    
    return filtered;
  }, [publicCollections, publicSearch, publicTcgFilter, publicSortOrder]);

  // Manejadores
  const handleCreateCollection = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = '/auth'; return; }
    try {
      await axios.post(`${API}/collections`, createForm);
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', is_public: false, tcg_type: 'mixed' });
      fetchCollections();
    } catch (err) {
      console.error('Failed to create collection:', err);
    }
  };

  const handleSearchCards = async () => {
    if (!cardType) return;
    setSearchLoading(true);
    try {
      const { data } = await axios.get(`${API}/tcg/catalog`, {
        params: {
          tcg_type: cardType,
          q: cardSearch.trim(),
          page: 1,
          page_size: 20,
        },
      });
      setSearchResults(data.cards || []);
    } catch { setSearchResults([]); }
    setSearchLoading(false);
  };

  const selectedCollectionTcg = selectedCollection?.tcg_type || 'mixed';
  const availableCardTypes = selectedCollectionTcg === 'mixed'
    ? TCG_OPTIONS
    : TCG_OPTIONS.filter((type) => type === selectedCollectionTcg);

  useEffect(() => {
    if (!availableCardTypes.includes(cardType)) {
      setCardType(availableCardTypes[0] || 'pokemon');
    }
  }, [availableCardTypes, cardType]);

  useEffect(() => {
    if (!showCardSearch || !selectedCollection || !cardType) return;
    handleSearchCards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCardSearch, selectedCollection?.collection_id, cardType]);

  const handleAddCard = async (card, forceAdd = false) => {
    if (!selectedCollection) return;
    
    const existingCard = selectedCollection.cards?.find(c => c.external_id === card.external_id);
    if (existingCard && !forceAdd) {
      const confirmAdd = window.confirm(
        text(
          `"${card.name}" ya está en tu colección (${existingCard.quantity}x). ¿Añadir otra copia?`,
          `"${card.name}" is already in your collection (${existingCard.quantity}x). Add another copy?`,
        )
      );
      if (!confirmAdd) return;
    }
    
    try {
      await axios.post(`${API}/collections/${selectedCollection.collection_id}/cards`, {
        external_id: card.external_id, name: card.name, api_provider: card.api_provider,
        image_url: card.image_url, set_name: card.set_name, rarity: card.rarity,
        type_line: card.type_line, quantity: 1
      });
      
      setAddedCards(prev => new Set([...prev, card.external_id]));
      
      const newQty = existingCard ? existingCard.quantity + 1 : 1;
      toast.success(text(`"${card.name}" añadida (${newQty}x)`, `"${card.name}" added (${newQty}x)`));
      
      const { data } = await axios.get(`${API}/collections/${selectedCollection.collection_id}`);
      setSelectedCollection(data);
      fetchCollections();
      
      setTimeout(() => {
        setShowCardSearch(false);
        setSearchResults([]);
        setCardSearch('');
        setAddedCards(new Set());
      }, 800);
    } catch (err) {
      toast.error(text('Error al añadir carta', 'Error adding card'));
    }
  };

  const handleAddMultipleCards = async (cards) => {
    if (!selectedCollection || cards.length === 0) return;
    let added = 0;
    for (const card of cards) {
      try {
        await axios.post(`${API}/collections/${selectedCollection.collection_id}/cards`, {
          external_id: card.external_id, name: card.name, api_provider: card.api_provider,
          image_url: card.image_url, set_name: card.set_name, rarity: card.rarity,
          type_line: card.type_line, quantity: 1
        });
        added++;
      } catch { /* skip individual errors */ }
    }
    try {
      const { data } = await axios.get(`${API}/collections/${selectedCollection.collection_id}`);
      setSelectedCollection(data);
      fetchCollections();
    } catch { /* ignore refresh error */ }
    toast.success(text(`${added} carta(s) añadidas`, `${added} card(s) added`));
    setShowCardSearch(false);
    setSearchResults([]);
    setCardSearch('');
    setAddedCards(new Set());
  };

  const handleRemoveCard = async (externalId) => {
    if (!selectedCollection) return;
    try {
      await axios.delete(`${API}/collections/${selectedCollection.collection_id}/cards/${externalId}`);
      const { data } = await axios.get(`${API}/collections/${selectedCollection.collection_id}`);
      setSelectedCollection(data);
      fetchCollections();
    } catch (err) {
      console.error('Failed to remove card:', err);
    }
  };

  const handleDeleteCollection = async (colId) => {
    try {
      await axios.delete(`${API}/collections/${colId}`);
      setSelectedCollection(null);
      fetchCollections();
    } catch (err) {
      console.error('Failed to delete collection:', err);
    }
  };

  const togglePublic = async (col) => {
    try {
      await axios.put(`${API}/collections/${col.collection_id}`, { is_public: !col.is_public });
      fetchCollections();
      if (selectedCollection?.collection_id === col.collection_id) {
        setSelectedCollection(prev => ({ ...prev, is_public: !prev.is_public }));
      }
    } catch (err) {
      console.error('Failed to toggle collection visibility:', err);
    }
  };

  const displayCollections = tab === 'my' ? myCollections : filteredPublicCollections;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-black text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('collections.title')}
          </h1>
          <p className="text-[#A8B5AE] mt-1">
            {text('Organiza y comparte tu colección digital de cartas TCG.', 'Organize and share your digital TCG card collection.')}
          </p>
        </div>
        {user && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2"
            data-testid="create-collection-btn"
          >
            <Plus size={16} />{t('collections.create')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button 
          onClick={() => { setTab('my'); setSelectedCollection(null); }} 
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
            tab === 'my' ? 'bg-[#C9A962] text-[#0A1612]' : 'bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6]'
          }`} 
          data-testid="my-collections-tab"
        >
          {t('collections.my_collections')}
        </button>
        <button 
          onClick={() => { setTab('public'); setSelectedCollection(null); }} 
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
            tab === 'public' ? 'bg-[#C9A962] text-[#0A1612]' : 'bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6]'
          }`} 
          data-testid="public-collections-tab"
        >
          {t('collections.public_collections')}
        </button>
      </div>

      {/* Public Filters */}
      {tab === 'public' && (
        <PublicFilters
          lang={lang}
          publicSearch={publicSearch}
          setPublicSearch={setPublicSearch}
          publicTcgFilter={publicTcgFilter}
          setPublicTcgFilter={setPublicTcgFilter}
          publicSortOrder={publicSortOrder}
          setPublicSortOrder={setPublicSortOrder}
          resultCount={filteredPublicCollections.length}
        />
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Collections List */}
        <CollectionList
          collections={displayCollections}
          selectedCollection={selectedCollection}
          onSelect={setSelectedCollection}
          tab={tab}
          user={user}
          lang={lang}
          t={t}
        />

        {/* Collection Detail */}
        <div className="lg:col-span-2">
          <CollectionDetail
            collection={selectedCollection}
            user={user}
            lang={lang}
            t={t}
            onTogglePublic={togglePublic}
            onAddCard={() => setShowCardSearch(true)}
            onDeleteCollection={handleDeleteCollection}
            onRemoveCard={handleRemoveCard}
          />
        </div>
      </div>

      {/* Create Collection Modal */}
      <CreateCollectionModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        lang={lang}
        t={t}
        form={createForm}
        setForm={setCreateForm}
        onSubmit={handleCreateCollection}
      />

      {/* Card Search Modal */}
      <CardSearchModal
        show={showCardSearch && selectedCollection}
        onClose={() => setShowCardSearch(false)}
        lang={lang}
        t={t}
        cardType={cardType}
        setCardType={setCardType}
        availableCardTypes={availableCardTypes}
        selectedCollectionTcg={selectedCollectionTcg}
        cardSearch={cardSearch}
        setCardSearch={setCardSearch}
        onSearch={handleSearchCards}
        searchLoading={searchLoading}
        searchResults={searchResults}
        addedCards={addedCards}
        onAddCard={handleAddCard}
        onAddMultiple={handleAddMultipleCards}
      />
    </div>
  );
}
